import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';

interface StateVsNationCompareProps {
  occupationTitle: string;
  occupationSlug: string;
  locationName: string;
  stateMedian: number;
  stateMean: number;
  nationalMedian?: number | null;
  nationalMean?: number | null;
  stateEmployment?: number | null;
  nationalEmployment?: number | null;
  stateRank?: number | null;
  stateCount?: number | null;
  stateP10?: number | null;
  stateP90?: number | null;
  className?: string;
}

const formatCurrency = (value?: number | null) =>
  value == null || value <= 0
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(Math.round(value));

const formatPct = (value: number) =>
  `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;

/**
 * Per-state differentiator: how this state's pay compares to the national
 * OEWS figures for the same occupation. Only renders on state pages when
 * both state and national medians are available.
 */
export default function StateVsNationCompare({
  occupationTitle,
  occupationSlug,
  locationName,
  stateMedian,
  stateMean,
  nationalMedian,
  nationalMean,
  stateEmployment,
  nationalEmployment,
  stateRank,
  stateCount,
  stateP10,
  stateP90,
  className = '',
}: StateVsNationCompareProps) {
  if (!nationalMedian || nationalMedian <= 0 || !stateMedian || stateMedian <= 0) {
    return null;
  }

  const medianDelta = ((stateMedian - nationalMedian) / nationalMedian) * 100;
  const meanDelta =
    nationalMean && stateMean
      ? ((stateMean - nationalMean) / nationalMean) * 100
      : null;
  const above = medianDelta >= 0;
  const shareOfJobs =
    stateEmployment && nationalEmployment && nationalEmployment > 0
      ? (stateEmployment / nationalEmployment) * 100
      : null;
  const spread =
    stateP10 && stateP90 && stateP10 > 0 && stateP90 > stateP10
      ? stateP90 - stateP10
      : null;

  return (
    <Card className={`border-primary/20 bg-primary/5 ${className}`}>
      <CardContent className="pt-6">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <BarChart3 className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 space-y-3">
            <h3 className="font-semibold">
              {locationName} vs. United States for {occupationTitle}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The median {occupationTitle.toLowerCase()} salary in {locationName} is{' '}
              <strong>{formatCurrency(stateMedian)}</strong>, which is{' '}
              <strong className={above ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}>
                {formatPct(medianDelta)}
              </strong>{' '}
              {above ? 'above' : 'below'} the national median of{' '}
              <strong>{formatCurrency(nationalMedian)}</strong>
              {meanDelta != null && (
                <>
                  {' '}
                  (mean pay is {formatPct(meanDelta)} {meanDelta >= 0 ? 'above' : 'below'} the
                  U.S. mean of {formatCurrency(nationalMean)})
                </>
              )}
              .
              {stateRank != null && stateCount != null && stateRank > 0 && (
                <>
                  {' '}
                  Among {stateCount} states and D.C. with OEWS data, {locationName} ranks{' '}
                  <strong>#{stateRank}</strong> by median wage for this occupation
                  {stateRank <= 10
                    ? ' (top tier)'
                    : stateRank >= stateCount - 9
                      ? ' (near the bottom of the distribution)'
                      : ''}
                  .
                </>
              )}
              {shareOfJobs != null && (
                <>
                  {' '}
                  {locationName} accounts for about {shareOfJobs.toFixed(1)}% of U.S.{' '}
                  {occupationTitle.toLowerCase()} employment in the latest OEWS release.
                </>
              )}
              {spread != null && (
                <>
                  {' '}
                  Within {locationName}, the 10th-to-90th percentile spread is{' '}
                  {formatCurrency(spread)} ({formatCurrency(stateP10)}–{formatCurrency(stateP90)}).
                </>
              )}
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-background rounded-lg border">
                <div className="text-muted-foreground">{locationName} median</div>
                <div className="text-lg font-bold">{formatCurrency(stateMedian)}</div>
              </div>
              <div className="p-3 bg-background rounded-lg border">
                <div className="text-muted-foreground">U.S. median</div>
                <div className="text-lg font-bold">{formatCurrency(nationalMedian)}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              See every state ranked for this occupation on the{' '}
              <Link
                to={`/best-states/${occupationSlug}/`}
                className="text-primary hover:underline"
              >
                best-paying states
              </Link>{' '}
              page, or compare purchasing power on{' '}
              <Link to="/real-wages/" className="text-primary hover:underline">
                real wages
              </Link>
              .
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
